import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';

import { HeaderNavTabs } from '@/components/HeaderNavTabs';
import { LIVE_BADGE_HEIGHT, LIVE_LABEL_WITH_BADGE_WIDTH, LiveBadge } from '@/components/LiveBadge';
import { SocialCurrencyToggle } from '@/components/SocialCurrencyToggle';
import { NEW_TOP_TAB_BAR_CONFIG, NEW_TOP_TAB_BAR_HEIGHT } from '@/components/TopTabBar';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { toTitleCase } from '@/utils/toTitleCase';

import { BetList } from '../components/BetList';
import { FinishedBetList } from '../components/FinishedBetList';
import { TabType } from '../hooks/types';
import { useLiveEntriesCount } from '../hooks/use-bets';

const styles = StyleSheet.create({
    // A custom `tabBarLabel` doesn't inherit NEW_TOP_TAB_BAR_CONFIG's label style, so mirror its full
    // vertical offset (marginTop 3 + bottom 2) or Live sits ~2px lower than the plain-string tabs.
    liveLabel: { marginTop: 3, bottom: 2 },
    hiddenSeparator: { width: 0 },
    liveTabLabelWithBadge: {
        width: LIVE_LABEL_WITH_BADGE_WIDTH,
    },
    badge: {
        height: LIVE_BADGE_HEIGHT,
        top: (NEW_TOP_TAB_BAR_HEIGHT - LIVE_BADGE_HEIGHT) / 2,
    },
});

type SECTION_KEYS = 'upcoming' | 'live' | 'unconfirmed' | 'finished';
type SECTION_SCREENS = `Bet-${SECTION_KEYS}`;

/** Route params consumed by `BetList` (Upcoming/Live/Unconfirmed); Finished manages its own filter so ignores these. */
type BetsTabParams = {
    tab: TabType;
    tabType: string;
};

type Section = BetsTabParams & {
    component: React.ComponentType;
};

type Sections = {
    [K in SECTION_KEYS]: Section;
};

export type BetsTabParamsList = {
    [K in SECTION_SCREENS]: BetsTabParams;
};

const SECTIONS: Sections = {
    upcoming: { tab: 'Upcoming', tabType: 'upcoming', component: BetList },
    live: { tab: 'Live', tabType: 'live', component: BetList },
    unconfirmed: { tab: 'Unmatched', tabType: 'unconfirmed', component: BetList },
    finished: { tab: 'All', tabType: 'finished', component: FinishedBetList },
};

const SECTIONS_ORDER: SECTION_KEYS[] = ['upcoming', 'live', 'finished'];

const PREDICTIONS_SECTIONS_ORDER: SECTION_KEYS[] = ['unconfirmed', 'upcoming', 'live', 'finished'];

const Tab = createMaterialTopTabNavigator<BetsTabParamsList>();

const renderLiveBadge = (liveCount: number) => <LiveBadge count={liveCount} style={styles.badge} />;

export const BetsScreen = () => {
    const { t } = useTranslation(['bets']);
    const isPredictions = useIsPredictionsProduct();
    const liveEntriesCount = useLiveEntriesCount();
    const sectionsOrder = isPredictions ? PREDICTIONS_SECTIONS_ORDER : SECTIONS_ORDER;

    return (
        <MaxWidthWrapper flex={1}>
            <HeaderNavTabs<BetsTabParamsList>
                title={isPredictions ? t('trades') : t('entries')}
                initialRouteName={`Bet-${sectionsOrder[0]}`}
                rightAccessory={isPredictions ? null : <SocialCurrencyToggle />}
                tabBarScrollEnabled={isPredictions}
            >
                {sectionsOrder.map(sectionKey => {
                    const { tab, tabType, component } = SECTIONS[sectionKey];
                    const title = toTitleCase(t(sectionKey));
                    const showLiveBadge = sectionKey === 'live' && liveEntriesCount > 0;
                    return (
                        <Tab.Screen
                            name={`Bet-${sectionKey}`}
                            key={`Bet-${tab}`}
                            options={{
                                title,
                                tabBarButtonTestID: 'betsScreenTabs',
                                tabBarLabelStyle: showLiveBadge
                                    ? [NEW_TOP_TAB_BAR_CONFIG.tabBarLabelStyle, styles.liveTabLabelWithBadge]
                                    : NEW_TOP_TAB_BAR_CONFIG.tabBarLabelStyle,
                                tabBarBadge: showLiveBadge ? () => renderLiveBadge(liveEntriesCount) : undefined,
                            }}
                            initialParams={{ tab, tabType }}
                            component={component}
                        />
                    );
                })}
            </HeaderNavTabs>
        </MaxWidthWrapper>
    );
};
